/* eslint-disable camelcase */
import type { AdditionalProductsSelections } from 'client/components/CheckoutContainer/types'

interface IPaymentMultiMethodRaw {
  paycomet_klarna?: string // Paycomet klarna
  paycomet_giropay?: string // Paycomet giropay
  paycomet_ideal?: string // Paycomet ideal
  paycomet_multibanco?: string // Paycomet multibanco
  paycomet_bancontact?: string // Paycomet bancontact
  credit_card: { provider?: string; params: { payment_url: string; token: string } }
  apple_pay?: { provider?: string; params: [] }
  worldline_direct: {
    payment_url: string
  } // legacy payment method included in multi-method response to aid crossover to new payment methods
}

interface IPaymentAPMRaw {
  paycomet_klarna?: string // Paycomet klarna
  paycomet_giropay?: string // Paycomet giropay
  paycomet_ideal?: string // Paycomet ideal
  paycomet_multibanco?: string // Paycomet multibanco
  paycomet_bancontact?: string // Paycomet bancontact
}

interface IPaymentMethodRevolutRaw extends IPaymentAPMRaw {
  revolut: {
    token: string
  }
  apple_pay?: { provider?: string; params?: [] }
}

interface IPaymentMethodWordlineRaw extends IPaymentAPMRaw {
  worldline_direct: {
    payment_url: string
  }
  apple_pay?: { provider?: string; params?: [] }
}

interface IPaymentMethodOgoneRaw extends IPaymentAPMRaw {
  // Ogone
  flexcheckout: {
    payment_url: string
  }
  apple_pay?: { provider?: string; params?: [] }
}

interface IPaymentMethodPaycometRaw extends IPaymentAPMRaw {
  paycomet: {
    jetID: string
  }
  apple_pay?: { provider?: string; params?: [] }
}

export type PaymentMethodRaw = IPaymentMethodOgoneRaw &
  IPaymentMethodWordlineRaw &
  IPaymentMethodPaycometRaw &
  IPaymentMultiMethodRaw &
  IPaymentMethodRevolutRaw

export enum PaymentMethodEnum {
  Ogone = 'ogone',
  Worldline = 'worldline_direct',
  Paycomet = 'paycomet',
  Revolut = 'revolut',
}

enum APMKindEnum {
  Klarna = 'klarna',
  Giropay = 'giropay',
  Ideal = 'ideal',
  Multibanco = 'multibanco',
  Bancontact = 'bancontact',
}

enum APMValuesEnum {
  Klarna = 'paycomet_klarna',
  Giropay = 'paycomet_giropay',
  Ideal = 'paycomet_ideal',
  Multibanco = 'paycomet_multibanco',
  Bancontact = 'paycomet_bancontact',
}

interface IAPM {
  kind: APMKindEnum
  value: APMValuesEnum
}

interface IPaymentMethodRevolut {
  kind: PaymentMethodEnum.Revolut
  creditCard: {
    token: string
  }
  applePay?: boolean
  apm?: IAPM[]
}

interface IPaymentMethodWorldline {
  kind: PaymentMethodEnum.Worldline
  creditCard: {
    url: string
  }
  applePay?: boolean
  apm?: IAPM[]
}

interface IPaymentMethodOgone {
  kind: PaymentMethodEnum.Ogone
  creditCard: {
    url: string
  }
  applePay?: boolean
  apm?: IAPM[]
}

interface IPaymentMethodPaycomet {
  kind: PaymentMethodEnum.Paycomet
  creditCard: {
    clientId: string
  }
  applePay?: boolean
  apm?: IAPM[]
}

export function mapAPMs(data: IPaymentAPMRaw): IAPM[] {
  const apm = []
  if (data.paycomet_klarna) {
    apm.push({ kind: APMKindEnum.Klarna, value: APMValuesEnum.Klarna })
  }
  if (data.paycomet_giropay) {
    apm.push({ kind: APMKindEnum.Giropay, value: APMValuesEnum.Giropay })
  }
  if (data.paycomet_ideal) {
    apm.push({ kind: APMKindEnum.Ideal, value: APMValuesEnum.Ideal })
  }
  if (data.paycomet_multibanco) {
    apm.push({ kind: APMKindEnum.Multibanco, value: APMValuesEnum.Multibanco })
  }
  if (data.paycomet_bancontact) {
    apm.push({ kind: APMKindEnum.Bancontact, value: APMValuesEnum.Bancontact })
  }
  return apm
}

// map credit card (multi-method payments)
function mapRevolut(data: IPaymentMultiMethodRaw): IPaymentMethodRevolut {
  return {
    kind: PaymentMethodEnum.Revolut,
    creditCard: {
      token: data.credit_card.params.token,
    },
    applePay: !!data.apple_pay?.provider,
    apm: mapAPMs(data),
  }
}

function mapWorldline(data: IPaymentMultiMethodRaw): IPaymentMethodWorldline {
  return {
    kind: PaymentMethodEnum.Worldline,
    creditCard: {
      url: data.credit_card.params.payment_url,
    },
    applePay: !!data.apple_pay?.provider,
    apm: mapAPMs(data),
  }
}

// map legacy payment methods
function mapOgone(data: IPaymentMethodOgoneRaw): IPaymentMethodOgone {
  return {
    kind: PaymentMethodEnum.Ogone,
    creditCard: {
      url: data.flexcheckout.payment_url,
    },
    applePay: !!data.apple_pay?.provider,
    apm: mapAPMs(data),
  }
}

function mapPaycomet(data: IPaymentMethodPaycometRaw): IPaymentMethodPaycomet {
  return {
    kind: PaymentMethodEnum.Paycomet,
    creditCard: {
      clientId: data.paycomet.jetID,
    },
    applePay: !!data.apple_pay?.provider,
    apm: mapAPMs(data),
  }
}

export type PaymentMethods =
  | IPaymentMethodOgone
  | IPaymentMethodWorldline
  | IPaymentMethodPaycomet
  | IPaymentMethodRevolut

export function mapPaymentMethods(data: PaymentMethodRaw): PaymentMethods {
  // multi-method payment method
  if (data.credit_card?.provider === PaymentMethodEnum.Worldline) {
    return mapWorldline(data)
  }
  if (data.credit_card?.provider === PaymentMethodEnum.Revolut) {
    return mapRevolut(data)
  }

  // older payment methods
  if (data.flexcheckout?.payment_url) {
    return mapOgone(data)
  }
  if (data.paycomet?.jetID) {
    return mapPaycomet(data)
  }

  throw new Error('Invalid payment method')
}

export function mapAdditionalProductsPurchaseDetails(
  additional_products: AdditionalProductsSelections | [] | undefined
): { additionalProductsSelections?: AdditionalProductsSelections } {
  if (!additional_products || Array.isArray(additional_products)) {
    return { additionalProductsSelections: {} }
  }

  return {
    additionalProductsSelections: additional_products,
  }
}
